import React from "react";
import { emailSvc } from "../email-automation/common";

/*
 * Email Journeys shared bits (EMAIL_JOURNEYS.md; UX = EMAIL_JOURNEYS_UX.md).
 * API: /v1/campaign/email/journeys on CampaignService. A journey is a TREE
 * of email / delay (wait) / branch / exit (end) nodes — branches split and
 * never rejoin, so the whole thing renders as nested step columns and no
 * free-form canvas is needed. Email steps are authored with the section
 * composer (design_json) — the worker's ComposerSend renders per send.
 */

export function journeySvc(api = "") {
  return emailSvc(`/journeys${api}`);
}
export const NODE_META = {
  email: {
    label: "Email",
    color: "var(--plk-info-text)",
    bg: "var(--plk-info-soft)"
  },
  delay: {
    label: "Wait",
    color: "var(--plk-warning-text)",
    bg: "var(--plk-warning-soft)"
  },
  branch: {
    label: "Branch",
    color: "var(--plk-info-text)",
    bg: "var(--plk-info-soft)"
  },
  exit: {
    label: "End",
    color: "var(--plk-text-dim)",
    bg: "var(--plk-surface-alt)"
  },
  tag: {
    label: "Tag",
    color: "var(--plk-success-text)",
    bg: "var(--plk-success-soft)"
  }
};
export const CONDITION_KINDS = [["converted", "Converted", "Any conversion since they entered the journey (or within the window)."], ["openedLastEmail", "Opened the last email", "Opened the most recent email this journey sent them."], ["eventOccurred", "Did an event", "A named conversion or engagement event from your site tracking."], ["inAudience", "Is in an audience", "Currently a member of one of your audiences."], ["lifecycleStage", "Lifecycle stage is", "The customer record's lifecycle stage (lead, prospect, customer…) equals this value."], ["assignedRep", "Assigned rep is", "The customer record's assigned rep equals this rep id."]];

// Quiet-hours timezone (2026-09-27): when set, the quiet window is read in this zone instead of UTC.
export const TZ_OPTIONS = [["", "UTC"], ["America/New_York", "Eastern (New York)"], ["America/Chicago", "Central (Chicago)"], ["America/Denver", "Mountain (Denver)"], ["America/Phoenix", "Arizona (Phoenix)"], ["America/Los_Angeles", "Pacific (Los Angeles)"], ["America/Anchorage", "Alaska"], ["Pacific/Honolulu", "Hawaii"], ["America/Toronto", "Toronto"], ["America/Vancouver", "Vancouver"], ["Europe/London", "London"], ["Europe/Paris", "Paris"], ["Europe/Berlin", "Berlin"], ["Australia/Sydney", "Sydney"]];
export const GOAL_KINDS = [["", "No goal: people finish the steps"], ["conversion", "Any conversion"], ["eventOccurred", "A specific event…"]];
export const TRIGGER_KINDS = [["ProductViewed", "Products viewed", "Visitors who viewed products but didn't convert."], ["CartAbandoned", "Cart abandoned", "Visitors who added to cart but didn't purchase. (Today this reaches the viewed-product population: real cart events aren't wired in yet.)"], ["CustomerLoyalty", "Repeat customer", "Customers with multiple purchases."], ["WelcomeSeries", "New contact", "New contacts with an email address: enrolled once, ever."], ["CustomerLapsed", "Lapsed customer", "Customers whose last order is older than the lookback (default 90 days): win-back and reorder reminders. Event name narrows to retail or wholesale orders; leave it blank for any."], ["CustomerLapseRisk", "Lapse risk", "Customers the lapse model moved into Watch, At risk or Lapsing, judged against each account's own ordering cadence instead of a fixed number of days. Pick the level that starts the journey."], ["custom", "Custom event", "Any conversion or engagement event your site tracks."]];

// CustomerLapseRisk levels (the lapse model's vocabulary; "healthy" never triggers).
export const LAPSE_RISK_LEVELS = [["watch", "Watch"], ["at_risk", "At risk"], ["lapsing", "Lapsing"]];
export function lapseRiskLevelLabel(level) {
  const hit = LAPSE_RISK_LEVELS.find(([k]) => k === String(level || "").toLowerCase());
  return hit ? hit[1] : "";
}
export function triggerText(j) {
  const tc = j && j.triggerConfig || {};
  const known = TRIGGER_KINDS.find(([k]) => k === (j && j.triggerType));
  if (known && known[0] === "CustomerLapseRisk") return `${known[1]}: ${lapseRiskLevelLabel(tc.level) || "At risk"}`;
  if (known) return known[1];
  if (tc.eventName) return `${tc.eventName} (${String(j.triggerType || "event").toLowerCase()})`;
  return j && j.triggerType || "—";
}
export function enrollmentChipStyle(status) {
  if (status === "Active") return {
    background: "var(--plk-info-soft)",
    color: "var(--plk-info-text)"
  };
  if (status === "Completed" || status === "ExitedGoal") return {
    background: "var(--plk-success-soft)",
    color: "var(--plk-success-text)"
  };
  if (status === "Failed") return {
    background: "var(--plk-danger-soft)",
    color: "var(--plk-danger-text)"
  };
  return {
    background: "var(--plk-surface-alt)",
    color: "var(--plk-text-dim)"
  };
}
export function enrollmentLabel(status) {
  return {
    Active: "in progress",
    Completed: "completed",
    ExitedGoal: "reached goal",
    ExitedOptOut: "opted out",
    Failed: "failed",
    Cancelled: "cancelled"
  }[status] || String(status || "").toLowerCase();
}

// ---- graph helpers ----------------------------------------------------

export function emptyGraph() {
  return {
    startNodeId: null,
    nodes: [],
    settings: {
      quietStartHourUtc: null,
      quietEndHourUtc: null,
      quietTimezone: null,
      dailyEmailCap: null
    }
  };
}
export function newId(graph) {
  let max = 0;
  (graph.nodes || []).forEach(n => {
    const m = /^n(\d+)$/.exec(n.id || "");
    if (m) max = Math.max(max, Number(m[1]));
  });
  return `n${max + 1}`;
}
export function nodeById(graph, id) {
  return (graph.nodes || []).find(n => n.id === id) || null;
}
export function newNode(graph, type, patch) {
  const base = {
    id: newId(graph),
    type,
    name: ""
  };
  const defaults = {
    email: {
      emailSubject: "",
      emailBody: "",
      designJson: null,
      containerId: null,
      nextNodeId: null
    },
    delay: {
      delayDays: 1,
      delayHours: 0,
      delayMinutes: 0,
      nextNodeId: null
    },
    branch: {
      condition: {
        kind: "converted",
        eventName: "",
        audienceId: "",
        windowDays: null
      },
      yesNodeId: null,
      noNodeId: null
    },
    exit: {},
    tag: {
      tagAudienceId: "",
      tagName: "",
      nextNodeId: null
    }
  };
  return {
    ...base,
    ...(defaults[type] || {}),
    ...(patch || {})
  };
}

// slot = null (the start) | { parentId, edge: "next" | "yes" | "no" }
export function slotTarget(graph, slot) {
  if (!slot) return graph.startNodeId || null;
  const p = nodeById(graph, slot.parentId);
  if (!p) return null;
  return (slot.edge === "yes" ? p.yesNodeId : slot.edge === "no" ? p.noNodeId : p.nextNodeId) || null;
}
export function setSlot(graph, slot, targetId) {
  if (!slot) return {
    ...graph,
    startNodeId: targetId
  };
  return {
    ...graph,
    nodes: graph.nodes.map(n => {
      if (n.id !== slot.parentId) return n;
      if (slot.edge === "yes") return {
        ...n,
        yesNodeId: targetId
      };
      if (slot.edge === "no") return {
        ...n,
        noNodeId: targetId
      };
      return {
        ...n,
        nextNodeId: targetId
      };
    })
  };
}

// Insert a node into a slot. Whatever the slot pointed at continues after
// the new node (a branch keeps it on the YES path). An "exit" can only be
// placed at the end of a path — the UI never offers it mid-path.
export function insertNode(graph, slot, type, patch) {
  const old = slotTarget(graph, slot);
  const node = newNode(graph, type, patch);
  if (type === "branch") node.yesNodeId = old;else if (type !== "exit") node.nextNodeId = old;
  let g = {
    ...graph,
    nodes: [...(graph.nodes || []), node]
  };
  g = setSlot(g, slot, node.id);
  return {
    graph: g,
    id: node.id
  };
}
export function parentSlotOf(graph, id) {
  if (graph.startNodeId === id) return null;
  for (const n of graph.nodes || []) {
    if (n.nextNodeId === id) return {
      parentId: n.id,
      edge: "next"
    };
    if (n.yesNodeId === id) return {
      parentId: n.id,
      edge: "yes"
    };
    if (n.noNodeId === id) return {
      parentId: n.id,
      edge: "no"
    };
  }
  return undefined; // orphan
}
export function subtreeIds(graph, id, acc) {
  const out = acc || new Set();
  const n = nodeById(graph, id);
  if (!n || out.has(id)) return out;
  out.add(id);
  [n.nextNodeId, n.yesNodeId, n.noNodeId].filter(Boolean).forEach(c => subtreeIds(graph, c, out));
  return out;
}

// Email/wait: splice out (the path continues). Branch: removes both paths
// below it too. End: just clears the edge (path becomes "completes").
export function removeNode(graph, id) {
  const n = nodeById(graph, id);
  if (!n) return graph;
  const slot = parentSlotOf(graph, id);
  if (slot === undefined) return {
    ...graph,
    nodes: graph.nodes.filter(x => x.id !== id)
  };
  let g = graph;
  let drop;
  if (n.type === "branch") {
    drop = subtreeIds(graph, id);
    g = setSlot(g, slot, null);
  } else {
    drop = new Set([id]);
    g = setSlot(g, slot, n.type === "exit" ? null : n.nextNodeId || null);
  }
  return {
    ...g,
    nodes: g.nodes.filter(x => !drop.has(x.id))
  };
}
export function sameSlot(a, b) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return a.parentId === b.parentId && a.edge === b.edge;
}

// Drag-and-drop move for single-child steps (email / wait): splice the node
// out of its current position, then insert it into `slot` — whatever the slot
// pointed at continues after the moved step. Branch and end steps don't move
// (a branch carries two subtrees; an end just marks a path's finish).
export function moveNode(graph, id, slot) {
  const n = nodeById(graph, id);
  if (!n || n.type === "branch" || n.type === "exit") return graph;
  const cur = parentSlotOf(graph, id);
  if (cur === undefined || sameSlot(cur, slot)) return graph;
  if (slot && slot.parentId === id) return graph;
  let g = removeNode(graph, id);
  const old = slotTarget(g, slot);
  g = {
    ...g,
    nodes: [...g.nodes, {
      ...n,
      nextNodeId: old
    }]
  };
  return setSlot(g, slot, id);
}
export function updateNode(graph, id, patch) {
  return {
    ...graph,
    nodes: graph.nodes.map(n => n.id === id ? {
      ...n,
      ...patch
    } : n)
  };
}
export function pathLength(graph) {
  // longest path (in steps) — a rough size for the list
  const walk = (id, depth) => {
    const n = nodeById(graph, id);
    if (!n || depth > 50) return depth;
    if (n.type === "branch") return Math.max(walk(n.yesNodeId, depth + 1), walk(n.noNodeId, depth + 1));
    if (n.type === "exit") return depth + 1;
    return walk(n.nextNodeId, depth + 1);
  };
  return walk(graph.startNodeId, 0);
}
export function delayText(n) {
  const parts = [];
  if (Number(n.delayDays)) parts.push(`${Number(n.delayDays)} day${Number(n.delayDays) === 1 ? "" : "s"}`);
  if (Number(n.delayHours)) parts.push(`${Number(n.delayHours)} hr`);
  if (Number(n.delayMinutes)) parts.push(`${Number(n.delayMinutes)} min`);
  return parts.length ? `Wait ${parts.join(" ")}` : "Wait (no duration yet)";
}
export function conditionText(n, audiences) {
  const c = n.condition || {};
  const win = c.windowDays ? ` in the last ${c.windowDays} days` : "";
  switch (c.kind) {
    case "converted":
      return `Converted${win}?`;
    case "openedLastEmail":
      return "Opened the last email?";
    case "eventOccurred":
      return c.eventName ? `Did "${c.eventName}"${win}?` : "Did an event (pick one)?";
    case "inAudience":
      {
        const a = (audiences || []).find(x => x.id === String(c.audienceId));
        return a ? `In audience "${a.name}"?` : "In an audience (pick one)?";
      }
    case "lifecycleStage":
      return c.value ? `Lifecycle stage is "${c.value}"?` : "Lifecycle stage is… (pick one)?";
    case "assignedRep":
      return c.value ? `Assigned rep is "${c.value}"?` : "Assigned rep is… (enter one)?";
    default:
      return "Condition (choose one)";
  }
}
export function nodeTitle(n, audiences) {
  if (!n) return "";
  switch (n.type) {
    case "email":
      return n.name || n.emailSubject || "Email (no subject yet)";
    case "delay":
      return delayText(n);
    case "branch":
      return n.name || conditionText(n, audiences);
    case "exit":
      return "End of journey";
    case "tag":
      return `Add to ${tagListName(n, audiences)}`;
    default:
      return n.type;
  }
}
export function nodeSubtitle(n) {
  if (!n) return "";
  if (n.type === "email") return n.name && n.emailSubject ? `“${n.emailSubject}”` : n.designJson ? "composed" : n.emailBody ? "HTML body" : "no content yet";
  return "";
}
export function emailNodes(graph) {
  return (graph.nodes || []).filter(n => n.type === "email");
}

// Quiet-hours helper: UTC hour → local clock label
export function utcHourLocal(h) {
  if (h === null || h === undefined || h === "") return "";
  const d = new Date();
  d.setUTCHours(Number(h), 0, 0, 0);
  return d.toLocaleTimeString([], {
    hour: "numeric"
  });
}
export function Chip({
  style,
  children,
  title
}) {
  return /*#__PURE__*/React.createElement("span", {
    className: "plk-chip",
    style: style,
    title: title
  }, children);
}

// ---- plain-language helpers (Email redesign 2026-10-09: Journeys list + journey page) ----

export function capFirst(s) {
  s = String(s || "");
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}
const EVENT_CLAUSES = {
  checkout: "someone starts checkout",
  cart: "someone adds something to their cart",
  product: "someone looks at a product",
  contactcreated: "someone new joins your contacts",
  signup: "someone signs up",
  purchase: "someone places an order",
  order: "someone places an order"
};

// What starts a journey, as the end of "When …" / "Starts when …" (lower case, no trailing period).
// Works for journeys and single emails (both carry triggerType + triggerConfig).
export function triggerClause(j) {
  const t = j && j.triggerType || "";
  const tc = j && j.triggerConfig || {};
  const ev = String(tc.eventName || "").trim();
  switch (t) {
    case "WelcomeSeries":
      return "someone new joins your contacts";
    case "ProductViewed":
      return "someone looks at a product and doesn't buy";
    case "CartAbandoned":
      return "someone leaves something in their cart";
    case "CustomerLoyalty":
      return "a customer buys again";
    case "CustomerLapsed":
      return tc.lookbackDays ? `a customer hasn't ordered in ${tc.lookbackDays} days` : "a customer hasn't ordered in a while";
    case "CustomerLapseRisk":
      return `a customer's ordering slows down (${(lapseRiskLevelLabel(tc.level) || "At risk").toLowerCase()})`;
    default:
      break;
  }
  if (ev) return EVENT_CLAUSES[ev.toLowerCase()] || `someone does "${ev}"`;
  if (t === "Conversion") return "someone converts";
  if (t === "Engagement") return "someone takes an action on your site";
  const txt = triggerText(j);
  return txt && txt !== "—" ? `this happens: ${txt.toLowerCase()}` : "its trigger fires";
}
export function triggerSentence(j) {
  return `When ${triggerClause(j)}`;
}

// A single email's send timing (scheduleConfig) in plain words.
export function waitPhrase(sc) {
  if (!sc || sc.sendImmediately) return "Sent right away";
  const parts = [];
  if (Number(sc.delayDays)) parts.push(`${Number(sc.delayDays)} day${Number(sc.delayDays) === 1 ? "" : "s"}`);
  if (Number(sc.delayHours)) parts.push(`${Number(sc.delayHours)} hour${Number(sc.delayHours) === 1 ? "" : "s"}`);
  if (Number(sc.delayMinutes)) parts.push(`${Number(sc.delayMinutes)} minute${Number(sc.delayMinutes) === 1 ? "" : "s"}`);
  return parts.length ? `Wait ${parts.join(" ")}` : "Sent right away";
}

// A wait step's length in words ("2 days", "3 hours"), no "Wait" prefix.
export function delayDuration(n) {
  const parts = [];
  if (Number(n && n.delayDays)) parts.push(`${Number(n.delayDays)} day${Number(n.delayDays) === 1 ? "" : "s"}`);
  if (Number(n && n.delayHours)) parts.push(`${Number(n.delayHours)} hour${Number(n.delayHours) === 1 ? "" : "s"}`);
  if (Number(n && n.delayMinutes)) parts.push(`${Number(n.delayMinutes)} minute${Number(n.delayMinutes) === 1 ? "" : "s"}`);
  return parts.join(" ");
}

// The journey's main path as short step labels: ["Welcome email", "Wait 2 days", …].
// Follows the "yes" side of a branch after naming the question. `max` caps the list; the
// return carries how many were left off.
export function journeyStepLabels(graph, audiences, max) {
  const out = [];
  const seen = new Set();
  let id = graph && graph.startNodeId;
  let total = 0;
  while (id && !seen.has(id)) {
    seen.add(id);
    const n = nodeById(graph, id);
    if (!n || n.type === "exit") break;
    total++;
    if (!max || out.length < max) {
      if (n.type === "delay") {
        const d = delayDuration(n);
        out.push(d ? `Wait ${d}` : "Wait");
      } else if (n.type === "branch") out.push(n.name || conditionText(n, audiences));else if (n.type === "tag") out.push(`Tag: ${tagListName(n, audiences)}`);else out.push(nodeTitle(n, audiences));
    }
    id = n.type === "branch" ? n.yesNodeId || n.noNodeId : n.nextNodeId;
  }
  return {
    labels: out,
    more: Math.max(0, total - out.length)
  };
}

// "3 days ago", "just now", "2 months ago"; "" when unknown.
export function agoText(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!isFinite(t)) return "";
  const s = Math.max(0, Date.now() - t);
  const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"} ago`;
  if (s < 3600000) return "just now";
  if (s < 86400000) return plural(Math.round(s / 3600000), "hour");
  const d = Math.round(s / 86400000);
  if (d < 60) return plural(d, "day");
  return plural(Math.round(d / 30), "month");
}
export function initials(name, email) {
  const src = String(name || "").trim();
  if (src) {
    const parts = src.split(/\s+/).filter(Boolean);
    return ((parts[0] || "")[0] + ((parts.length > 1 ? parts[parts.length - 1] : parts[0].slice(1))[0] || "")).toUpperCase();
  }
  const local = String(email || "").split("@")[0].replace(/[^a-z0-9]/gi, "");
  return (local.slice(0, 2) || "?").toUpperCase();
}

// Plain status word for a journey or single email.
export function statusWord(status) {
  return {
    Running: "Running",
    Paused: "Paused",
    Draft: "Draft",
    Archived: "Archived",
    Failed: "Stopped"
  }[status] || capFirst(String(status || "").toLowerCase());
}

// Tag step (2026-10-08): the list a "tag" node adds people to, by name. Prefers the
// current list name, then the name saved on the node, then a prompt to pick one.
export function tagListName(n, audiences) {
  if (!n || !n.tagAudienceId) return "a list (pick one)";
  const a = (audiences || []).find(x => String(x.id) === String(n.tagAudienceId));
  return a && a.name || n.tagName || "a list";
}

// A journey that is really a single email: one email step and nothing else (waits, splits and tags make it a journey).
export function isSingleJourney(graph) {
  const nodes = (graph && graph.nodes || []).filter(n => n && n.type !== "exit");
  return nodes.length === 1 && nodes[0].type === "email";
}